import React from 'react';
import { Box, Typography, Icons, IconButton } from '@ublend-npm/aulaui-core';
import { Dialog, Button } from '@mui/material';
import { useTheme } from '@ublend-npm/aulaui-core';

export interface RecentUpdatesInfoPopupProps {
  open: boolean;
  onClose: () => void;
}

const RecentUpdatesInfoPopup: React.FC<RecentUpdatesInfoPopupProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();

  const isDark = theme.palette.mode === 'dark';
  const dialogBg = theme.palette.background.paper;
  const titleColor = theme.palette.primary.main;
  const closeIconColor = theme.palette.text.secondary;
  const headingColor = theme.palette.text.primary;
  const descriptionColor = theme.palette.text.secondary;

  const educatorBulletColor = theme.palette.secondary.light;
  const studentBulletColor = theme.palette.primary.light;

  const btnBg = theme.palette.primary.main;
  const btnTextColor = theme.palette.primary.contrastText;
  const btnTextHoverColor = theme.palette.text.primary;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="recent-updates-info-modal-title"
      PaperProps={{
        sx: {
          bgcolor: dialogBg,
          borderRadius: 2,
          py: 3,
          px: 2,
          gap: 2,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="medium"
          sx={{ color: titleColor }}
          id="recent-updates-info-modal-title"
        >
          Recent Updates
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close information dialog"
          size="small"
          sx={{
            color: closeIconColor,
            '&:hover': {
              color: theme.palette.text.primary,
            },
          }}
        >
          <Icons.Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: educatorBulletColor,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="subtitle1"
              fontWeight="medium"
              sx={{ color: headingColor }}
            >
              Educators post
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: descriptionColor,
              lineHeight: 1.6,
            }}
          >
            The color coding highlights posts shared by educators across the
            Common Room and Announcements. This helps users quickly identify
            official updates and important communication at a glance.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: studentBulletColor,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="subtitle1"
              fontWeight="medium"
              sx={{ color: headingColor }}
            >
              Student post
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: descriptionColor,
              lineHeight: 1.6,
            }}
          >
            The color coding highlights posts shared by learners in Common Room
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: btnBg,
            color: btnTextColor,
            fontWeight: 'medium',
            textTransform: 'none',
            '&:hover': {
              ...(isDark && { color: btnTextHoverColor }),
            },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default RecentUpdatesInfoPopup;
